import React, { useContext, useEffect, useState } from 'react'
import { usePrices, PriceTable } from '../../../hooks/usePrices'
import css from './Pricing.module.css'
import Button from '../../buttons/Button/Button'
import { IconArrowRight } from '../../atoms/Icons/Icons'
import SegmentedInput from '../../inputs/SegmentedInput/SegmentedInput'
import { useNavigate } from 'react-router-dom'
import { useAuthState } from 'react-firebase-hooks/auth'
import { auth } from '../../../firebase'
import { UserContext } from '../../../context/UserContext'
import { AppContext } from '../../../context/AppContext'
import { goToCheckout } from '../../../services/goToCheckout'
import API from '../../../services'
import { Info } from 'react-feather'
import Confirm from '../../modals/Confirm'
import ActiveMember from '../../modals/ActiveMember'
import { Analytics } from '../../../services/analytics'
import { useURLParams } from '../../../hooks/useURLParams'

type Props = {
  onComplete: () => void
  isVisible?: boolean
  reSubscribing?: boolean
}

type PriceOption = 'monthly' | 'annually'

const Pricing = ({ onComplete, isVisible = true, reSubscribing }: Props) => {
  const { setSearchParams } = useURLParams()
  const { currentSpace } = useContext(AppContext)
  const [priceOption, setPriceOption] = useState<PriceOption>('annually')
  const [isLoading, setLoading] = useState([false, false, false])
  const { data: prices, isLoading: isPricesLoading } = usePrices()

  useEffect(() => {
    if (isVisible) {
      setSearchParams({ page: 'choose_price' })
    }
  }, [])

  const checkout = async (
    option:
      | 'first_monthly'
      | 'first_yearly'
      | 'second_monthly'
      | 'second_yearly'
      | 'third_monthly'
      | 'third_yearly'
  ) => {
    Analytics._logEvent({
      name: 'auth_pricing_plan_selected',
      params: { details: `plan selected: ${option}` },
    })

    if (option.match('first')) setLoading([true, false, false])
    else if (option.match('second')) setLoading([false, true, false])
    else setLoading([false, false, true])

    if (currentSpace) goToCheckout(option, currentSpace.id)
  }

  const [activeMemberInfoVisible, setActiveMemberInfoVisible] = useState(false)

  if (isPricesLoading) {
    return <div>Loading...</div>
  }

  return currentSpace ? (
    <>
      <ActiveMember
        isVisible={activeMemberInfoVisible}
        onDismiss={() => setActiveMemberInfoVisible(false)}
      />
      <div className={`${css['pricing']} ${reSubscribing && css['resub']}`}>
        <h1>
          {currentSpace?.title
            ? `Choose a Plan for ${currentSpace?.title}`
            : 'Choose Your Plan'}
        </h1>
        {!reSubscribing && (
          <h3 style={{ textAlign: 'center', margin: '0 0 0 0' }}>
            Starts with 14-day free trial
          </h3>
        )}
        <div className={css['segment-input']}>
          <SegmentedInput
            segments={[
              { label: 'Pay Annually', value: 'annually' },
              { label: 'Pay Monthly', value: 'monthly' },
            ]}
            value={priceOption}
            dark={!reSubscribing}
            onChange={(newValue) => setPriceOption(newValue as PriceOption)}
            style={{ height: '40px' }}
          />
        </div>

        <div className={css['pricing-options-container']}>
          <div className={css['pricing-options']}>
            <div className={`${css['pricing-plan']} ${css['left']}`}>
              <div className={css['padding']}>
                <span>Up to</span>
                <h2>
                  5 active members<span>*</span>
                </h2>
                {/* Feature list */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckSVG size={24} />
                      <div
                        style={{
                          paddingTop: '1px',
                          boxSizing: 'border-box',
                          height: '24px',
                        }}
                      >
                        Gym calendar
                      </div>
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckSVG size={24} />
                      <div
                        style={{
                          paddingTop: '1px',
                          boxSizing: 'border-box',
                          height: '24px',
                        }}
                      >
                        Workout tracking app
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                {prices && (
                  <div className={css['price']}>
                    <div
                      className={`${css['save']} ${
                        priceOption === 'annually' && css['visible']
                      }`}
                    >
                      Save $
                      {Math.floor(
                        12 * prices.first_monthly - prices.first_yearly
                      ).toFixed(2)}{' '}
                    </div>

                    <h1>
                      <>
                        <span>$ </span>
                        {priceOption === 'annually'
                          ? (prices.first_yearly / 12).toFixed(2)
                          : prices.first_monthly.toFixed(2)}
                        <span> /mo</span>
                      </>
                    </h1>

                    <p
                      className={`${css['sub-price']} ${
                        priceOption === 'annually' && css['visible']
                      }`}
                    >
                      {prices &&
                        `$ ${prices.first_yearly.toFixed(2)} paid annually`}
                    </p>
                    <p className={`${css['sub-price']} ${css['visible']}`}>
                      {prices &&
                        `$ ${prices.first_extra.toFixed(2)} per extra member*`}
                    </p>
                  </div>
                )}

                <Button
                  onClick={() =>
                    checkout(
                      priceOption === 'annually'
                        ? 'first_yearly'
                        : 'first_monthly'
                    )
                  }
                  isLoading={isLoading[0]}
                >
                  {!reSubscribing ? 'Try for Free' : 'Continue'}
                </Button>
              </div>
            </div>

            <div className={`${css['pricing-plan']} ${css['middle']}`}>
              <div className={css['padding']}>
                {/* <div className={css['highlight']}>Recommended</div> */}
                <span>Up to</span>
                <h2>
                  100 active members<span>*</span>
                </h2>

                {/* Feature list */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckSVG size={24} />
                      <div
                        style={{
                          paddingTop: '1px',
                          boxSizing: 'border-box',
                          height: '24px',
                        }}
                      >
                        Gym calendar
                      </div>
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckSVG size={24} />
                      <div
                        style={{
                          paddingTop: '1px',
                          boxSizing: 'border-box',
                          height: '24px',
                        }}
                      >
                        Workout tracking app
                      </div>
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckSVG size={24} />
                      <div
                        style={{
                          paddingTop: '1px',
                          boxSizing: 'border-box',
                          height: '24px',
                        }}
                      >
                        Programming cycles
                      </div>
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckSVG size={24} />
                      <div
                        style={{
                          paddingTop: '1px',
                          boxSizing: 'border-box',
                          height: '24px',
                        }}
                      >
                        Gym announcements
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                {prices && (
                  <div className={css['price']}>
                    <div
                      className={`${css['save']} ${
                        priceOption === 'annually' && css['visible']
                      }`}
                    >
                      Save $
                      {Math.floor(
                        12 * prices.second_monthly - prices.second_yearly
                      ).toFixed(2)}{' '}
                    </div>

                    <h1>
                      <>
                        <span>$ </span>
                        {priceOption === 'annually'
                          ? (prices.second_yearly / 12).toFixed(2)
                          : prices.second_monthly.toFixed(2)}
                        <span> /mo</span>
                      </>
                    </h1>

                    <p
                      className={`${css['sub-price']} ${
                        priceOption === 'annually' && css['visible']
                      }`}
                    >
                      {prices &&
                        `$ ${prices.second_yearly.toFixed(2)} paid annually`}
                    </p>
                    <p className={`${css['sub-price']} ${css['visible']}`}>
                      {prices &&
                        `$ ${prices.second_extra.toFixed(2)} per extra member*`}
                    </p>
                  </div>
                )}

                <Button
                  onClick={() =>
                    checkout(
                      priceOption === 'annually'
                        ? 'second_yearly'
                        : 'second_monthly'
                    )
                  }
                  isLoading={isLoading[1]}
                >
                  {!reSubscribing ? 'Try for Free' : 'Continue'}
                </Button>
              </div>
            </div>

            <div className={`${css['pricing-plan']} ${css['right']}`}>
              <div className={css['padding']}>
                <span>Up to</span>
                <h2>
                  300 active members<span>*</span>
                </h2>
                {/* Feature list */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                  }}
                >
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckSVG size={24} />
                      <div
                        style={{
                          paddingTop: '1px',
                          boxSizing: 'border-box',
                          height: '24px',
                        }}
                      >
                        Gym calendar
                      </div>
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckSVG size={24} />
                      <div
                        style={{
                          paddingTop: '1px',
                          boxSizing: 'border-box',
                          height: '24px',
                        }}
                      >
                        Workout tracking app
                      </div>
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckSVG size={24} />
                      <div
                        style={{
                          paddingTop: '1px',
                          boxSizing: 'border-box',
                          height: '24px',
                        }}
                      >
                        Programming cycles
                      </div>
                    </div>
                  </div>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <CheckSVG size={24} />
                      <div
                        style={{
                          paddingTop: '1px',
                          boxSizing: 'border-box',
                          height: '24px',
                        }}
                      >
                        Gym announcements
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                {prices && (
                  <div className={css['price']}>
                    <div
                      className={`${css['save']} ${
                        priceOption === 'annually' && css['visible']
                      }`}
                    >
                      Save $
                      {Math.floor(
                        12 * prices.third_monthly - prices.third_yearly
                      ).toFixed(2)}{' '}
                    </div>

                    <h1>
                      <>
                        <span>$ </span>
                        {priceOption === 'annually'
                          ? (prices.third_yearly / 12).toFixed(2)
                          : prices.third_monthly.toFixed(2)}
                        <span> /mo</span>
                      </>
                    </h1>

                    <p
                      className={`${css['sub-price']} ${
                        priceOption === 'annually' && css['visible']
                      }`}
                    >
                      {prices &&
                        `$ ${prices.third_yearly.toFixed(2)} paid annually`}
                    </p>
                    <p className={`${css['sub-price']} ${css['visible']}`}>
                      {prices &&
                        `$ ${prices.third_extra.toFixed(2)} per extra member*`}
                    </p>
                  </div>
                )}

                <Button
                  onClick={() =>
                    checkout(
                      priceOption === 'annually'
                        ? 'third_yearly'
                        : 'third_monthly'
                    )
                  }
                  isLoading={isLoading[2]}
                >
                  {!reSubscribing ? 'Try for Free' : 'Continue'}
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className={css['extra']}>
          <div onClick={() => setActiveMemberInfoVisible(true)}>
            <Info color={'var(--accent)'} />
            <h3>*Who is considered an active member?</h3>
          </div>
        </div>
      </div>
    </>
  ) : (
    <>Error: No active gym</>
  )
}
export default Pricing

const CheckSVG = ({ size }: { size: number }) => {
  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 256 256"
        style={{
          width: '100%',
          height: '100%',
          display: 'inline-block',
          fill: 'var(--accent)',
          color:
            'var(--token-970fd48c-120b-4417-b1e7-49740f006e40, rgb(86, 242, 159))',
          flexShrink: 0,
        }}
        focusable="false"
        color="var(--token-970fd48c-120b-4417-b1e7-49740f006e40, rgb(86, 242, 159))"
      >
        <g color="var(--token-970fd48c-120b-4417-b1e7-49740f006e40, rgb(86, 242, 159))">
          <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm45.66,85.66-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32Z"></path>
        </g>
      </svg>
    </div>
  )
}
